import { Steps } from "antd"
import { CheckCheck, CheckCircle } from "lucide-react" // Check icon
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { OSLoader } from "~/components/base-loader"
import useGlobalStore from "~/store/global"
import { sleep } from "~/utils/sleep"

interface PeopleLoaderProps {
  name?: string
  people_names?: string[]
  account_name?: string
  dataReady?: boolean
}

const formatPeopleNames = (names: string[]) => {
  if (names.length === 0) return ""
  if (names.length === 1) return names[0]
  return names.slice(0, -1).join(", ") + " and " + names[names.length - 1]
}

const initialStepValue = [
  {
    title: (
      <p className="text-13 dark:text-dark-6 text-dark-2">
        Finalizing insights...
      </p>
    )
  }
]

const initialCurrentStep = {
  parent: 0,
  child: 0
}

export const PeopleLoader = ({
  name,
  people_names = [],
  dataReady
}: PeopleLoaderProps) => {
  const { t } = useTranslation()
  const tenantType = useGlobalStore((store) => store.tenant?.tenant_type)
  const isOSLite = tenantType?.includes("os_lite")
  const formattedPeople = people_names?.length
    ? formatPeopleNames(people_names)
    : ""
  const stepsValues = useMemo(
    () =>
      [
        name && {
          title: (
            <p className="text-13 dark:text-dark-6 text-dark-2">
              {t("label_pre_meeting_brief_AgentUiTitle1")}{" "}
              {name ? (
                <strong className="text-primary dark:text-primary-light">
                  {name}
                </strong>
              ) : (
                "the person"
              )}
            </p>
          )
        },
        name && {
          title: (
            <p className="text-13 dark:text-dark-6 text-dark-2">
              {t("label_pre_meeting_brief_AgentUiTitle2")}{" "}
              <strong className="text-primary dark:text-primary-light">
                {name}
              </strong>
            </p>
          )
        },
        !isOSLite &&
          name && {
            title: (
              <p className="text-13 dark:text-dark-6 text-dark-2">
                {t("label_pre_meeting_brief_AgentUiTitle3")}{" "}
                <strong className="text-primary dark:text-primary-light">
                  {name}
                </strong>
                &apos;s {t("label_knowledge_library")}
              </p>
            )
          },
        name && {
          title: (
            <p className="text-13 dark:text-dark-6 text-dark-2">
              {t("label_pre_meeting_brief_AgentUiTitle4")}{" "}
              <b className="text-primary dark:text-primary-light">{name}</b>
            </p>
          )
        },
        people_names?.length > 0 && {
          title: (
            <p className="text-13 dark:text-dark-6 text-dark-2">
              {t("label_pre_meeting_brief_AgentUiTitle5")} -{" "}
              <b className="text-primary dark:text-primary-light">
                {formattedPeople}
              </b>
            </p>
          )
        },
        people_names?.length > 0 && {
          title: (
            <p className="text-13 dark:text-dark-6 text-dark-2">
              {t("label_pre_meeting_brief_AgentUiTitle4")} -{" "}
              <b className="text-primary dark:text-primary-light">
                {formattedPeople}
              </b>
            </p>
          )
        },
        {
          title: (
            <p className="text-13 dark:text-dark-6 text-dark-2">
              {t("label_pre_meeting_brief_AgentUiTitle6")}
            </p>
          )
        }
      ].filter(Boolean),
    [name, formattedPeople, people_names, isOSLite, t]
  ) as {
    title: ReactNode
    children?: { title: ReactNode }[]
  }[]
  const [steps, setSteps] =
    useState<{ title: ReactNode; children?: { title: ReactNode }[] }[]>(
      initialStepValue
    )
  const [currentStep, setCurrentStep] = useState(initialCurrentStep)

  const initializeSteps = useCallback(async () => {
    if (!dataReady) return
    for (let i = 0; i < stepsValues.length; i++) {
      const { promise, clear } = sleep(600 * i)
      await promise
      clear()
      const _previousSteps = stepsValues.slice(0, i)
      const currentStep = {
        ...stepsValues[i],
        children: [] as any[]
      }
      const children = stepsValues[i].children
      if (children?.length) {
        for (let j = 0; j < children.length; j++) {
          const { promise, clear } = sleep(1200 * (j + i + 1))
          await promise
          clear()
          currentStep.children = [...currentStep.children, children[j]]
          setSteps([..._previousSteps, currentStep])
          setCurrentStep((prev) => ({ ...prev, child: j + 1 }))
        }
      } else {
        setSteps([..._previousSteps, currentStep])
        setCurrentStep({ parent: i + 1, child: 0 })
      }
      setCurrentStep({ parent: i + 1, child: 0 })
    }
  }, [stepsValues, dataReady])

  useEffect(() => {
    if (dataReady) {
      initializeSteps()
    }
    return () => {
      setSteps(initialStepValue)
      setCurrentStep(initialCurrentStep)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataReady])

  // TODO: Add loading stat

  return (
    <div
      className="w-full h-full border border-light-border dark:border-border rounded-lg flex flex-col p-4"
      data-testid="person-brief-loader"
    >
      <ul className="flex flex-col gap-4">
        {steps.map((step, index) => (
          <li
            key={index}
            className="text-xs flex flex-col gap-2"
            data-testid={`person-brief-loader-step-${index}`}
          >
            <div className="flex gap-2 items-center">
              {index + 1 < currentStep.parent ? (
                <div className="w-5 h-5 rounded-full flex items-center justify-center bg-primary">
                  <CheckCheck size={16} color="#fff" />
                </div>
              ) : (
                <OSLoader className="xs" />
              )}
              <span>{step.title}</span>
            </div>
            {step.children && (
              <Steps
                className="pl-8"
                direction="vertical"
                current={step.children.length}
                size="small"
                progressDot
                items={step.children.map((child) => ({
                  className: "h-8",
                  title: (
                    <div
                      className="flex gap-2 items-center"
                      data-testid={`person-brief-loader-step-${index}-child-${child.title}`}
                    >
                      {child.title}
                      {index + 1 < currentStep.parent ? (
                        <CheckCircle size={16} color="#10b981" />
                      ) : (
                        <OSLoader className="xs" />
                      )}
                    </div>
                  )
                }))}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
